/**
 * The name a reader knows a document by — MYAC-1343.
 *
 * The ingestion pipeline converts what it cannot read natively and renames the
 * converted twin: a spreadsheet becomes markdown (`x.xlsx` → `x.xlsx.md`), an
 * Office file becomes a PDF (`x.docx` → `x.docx.pdf`). Those are the ONLY two
 * shapes it produces (`ingestion-pipeline/src/pipeline/steps/convert_step.py`:
 * `xlsx|xls|csv` → `.md`, `doc|docx|ppt|pptx` → `.pdf`; anything else is refused
 * at ingestion), and the engine carries the converted name everywhere — impact
 * rows, file rows, the executive summary's chips, the change-control hand-off,
 * the tasks board.
 *
 * This is DISPLAY ONLY. The stored name is a key: the PDF export joins impacts to
 * files on it, the by-document grouping keys on it, and the engine derives the
 * workitem identity and the view row ids from it. Never write the result back
 * into data, never use it to look anything up, and never show it inside an input
 * that saves (the tasks board's editable document name), or the saved value stops
 * matching the analysis row it came from.
 *
 * Matched pairs only: a client file genuinely named `spec.rtf.pdf` keeps its
 * `.pdf`, because the pipeline never made that name. The backend's
 * `document_display_name` (core_models_services, MYAC-1272) applies the same
 * two-pair table to the playbook views; the source-side field it projects there
 * (`document_name` from `Document.og_filename`) is the eventual authority for the
 * impact analysis too, and this helper is the fallback until then.
 */
export function documentDisplayName(document: string | null | undefined): string {
  const name = (document ?? '').trim()
  return name
    .replace(/\.(docx?|pptx?)\.pdf$/i, '.$1')
    .replace(/\.(xlsx?|csv)\.md$/i, '.$1')
}

/**
 * The same rule applied INSIDE a longer text — MYAC-1343, "full front".
 *
 * The engine's workers name documents in their own sentences, not only in a
 * dedicated field: a discarded impact's `resolved_reason` reads "Read
 * 22-DOC-0062 - OnebyDoc2U-Accessory list.xlsx.md section accesories_list; the
 * change does not affect it because…". Relabelling a field would leave those.
 *
 * This is a DISPLAY normalisation of the model's prose, the same kind the front
 * already does with `stripEmDashes`, not a rewrite of what the engine said: only
 * the suffix our own ingestion added is removed, and only where it forms one of
 * the two pairs the pipeline produces. Pair it with `stripEmDashes` through
 * `displayProse` rather than calling it alone.
 */
export function documentDisplayNameInText(text: string | null | undefined): string {
  const value = text ?? ''
  if (!value) return ''
  return value
    .replace(/\.(docx?|pptx?)\.pdf\b/gi, '.$1')
    .replace(/\.(xlsx?|csv)\.md\b/gi, '.$1')
}
